/**
 * @description Service layer for the flight cancellation workflow.
 *              Wraps the ops-facing endpoints documented in Cancellation-flow
 *              (SMB-2905 / SMB-2906 / SMB-2907 / SMB-2908), engine paths under
 *              /be/v1/flight/cancel/*.
 *
 *              Customer-side endpoints (/approve, /decline) are wrapped here for
 *              completeness but are normally called from MyTrip, not the ops
 *              dashboard. /request is included for the gateway contract; the ops
 *              dashboard enters the flow on an already-created cancel row.
 *
 *              Each function generates a fresh x-trace-id per call (UUID v4)
 *              forwarded through apiClient. The backend also returns an
 *              errorReference on error envelopes (see parseCancelError in the
 *              workflow component) which the UI surfaces for support.
 *
 *              Path prefix assumed for the gateway: BE_ADMIN_BASE + /flight/cancel
 *              (engine: /be/v1/flight/cancel -- gateway team maps).
 *
 * @author: Azamuddin
 * @date: 05/27/2026
 */

//********IMPORTS STARTS*********/
import { BE_ADMIN_BASE } from "./environment";
// @ts-ignore -- apiClient is JS; named exports are valid at runtime
import { apiGet, apiPost, genTraceId } from "./apiClient";
//********IMPORTS ENDS*********/

//********CONSTANTS STARTS*********/
const CANCEL_BASE = `${BE_ADMIN_BASE}/flight/cancel`;
//********CONSTANTS ENDS*********/

//********TYPES STARTS*********/
export type CancelStatus =
    | "CANCEL_REQUESTED"
    | "QUOTE_PENDING"
    | "QUOTE_READY"
    | "SENT_TO_CUSTOMER"
    | "CUSTOMER_APPROVED"
    | "CANCELLING"
    | "SUPPLIER_CONFIRMED"
    | "REFUND_PENDING"
    | "CANCELLED"
    | "CANCEL_FAILED"
    | "QUOTE_EXPIRED"
    | "AUTO_CANCELLED"
    | "FAILED";

export type CancelPhase = "AUTO" | "OPS" | "GRACE";
export type PtrType = "Void" | "Refund";
export type CancelMode = "FULL_ITINERARY" | "PER_SECTOR";
export type CancelQuoteSource = "AUTO" | "MANUAL";
export type PaxType = "ADT" | "CHD" | "INF";
export type RefundOutcome = "REFUNDED" | "FAILED";

// Per-pax-type cancellation rate. The FE sends only baseAmount / taxes / markup /
// serviceFee / discount / penalty; the backend computes totalAmount, refundAmount,
// and the SMB-3206 fields (cancelMarkup / cancelServiceFee / seatAmount / seatNumber)
// and returns them for display. Refund formula (BE-owned, SMB-3206):
// refund = totalAmount - discount - penalty - serviceFee - cancelMarkup - cancelServiceFee,
// where totalAmount = base + taxes + markup + serviceFee + seatAmount.
export interface CancelPaxRate {
    baseAmount: number;
    taxes: number;
    markup: number;
    serviceFee: number;
    totalAmount: number;
    discount: number;
    penalty: number;
    refundAmount: number;
    ticketNumbers?: string[];
    // SMB-3206 -- BE-computed, display-only (never sent by the FE)
    cancelMarkup?: number;
    cancelServiceFee?: number;
    seatAmount?: number;
    seatNumber?: string | null; // comma-joined seat number(s)
}

export interface CancelSectorQuote {
    transactionId: string;
    paxRates: Partial<Record<PaxType, CancelPaxRate>>;
    totalBase?: number;
    totalTaxes?: number;
    totalMarkup?: number;
    totalServiceFee?: number;
    totalCancelMarkup?: number; // SMB-3206
    totalCancelServiceFee?: number; // SMB-3206
    totalDiscount?: number;
    totalPenalty?: number;
    totalVoidingFee?: number;
    totalRefund?: number;
    currency?: string;
}

export interface CancelBookingTotals {
    totalBase: number;
    totalTaxes: number;
    totalMarkup: number;
    totalServiceFee: number;
    totalCancelMarkup?: number; // SMB-3206
    totalCancelServiceFee?: number; // SMB-3206
    totalDiscount: number;
    totalPenalty: number;
    totalVoidingFee: number;
    totalRefund: number;
    currency: string;
    paxCount: Partial<Record<PaxType, number>>;
}

export interface CancelSector {
    transactionId: string;
    pnr?: string;
    source: string;
    destination: string;
    departureDate?: string;
    cabinClass?: string;
    totalFare?: number;
    baseFare?: number;
    taxes?: number;
    apportionedDiscount?: number;
    quoteSource?: CancelQuoteSource;
    isRefundable?: boolean;
    supplierCode?: number;
    supplierName?: string;
    mfUniqueId?: string;
    tripId?: string;
    productId?: string;
    orderId?: string;
    sectorId?: string;
    sectorUniqueKey?: string;
    quoteGroupKey?: string; // shared-PNR grouping key (round-trip = one group)
    tripType?: string;
    flowType?: string;
}

export interface CancelPassenger {
    firstName: string;
    lastName: string;
    title?: string;
    paxType: PaxType;
    email?: string | null;
    isLeadPax?: boolean;
    ticketNumber?: string;
    ticketStatus?: string;
    pnr?: string;
    transactionId?: string;
    fares?: {
        baseFare: number;
        taxes: number;
        markup: number;
        serviceFee: number;
        totalAmount: number;
        supplierBaseFare?: number;
        supplierTotalFare?: number;
    };
}

export interface CancelBookingInfo {
    itineraryId: string;
    bookingTotal?: number;
    supplierTotal?: number;
    paymentStatus?: string;
    itineraryStatus?: string;
    internalStatus?: string;
    flightAutoDiscount?: number;
    discountAmount?: number;
    flightDiscount?: number;
    finalPrice?: number;
}

export interface CancelQuoteDetails {
    quote?: {
        sectorQuotes: CancelSectorQuote[];
        bookingTotals: CancelBookingTotals;
    };
    executeAt?: string;
    // present in JSON for FE escalation rendering
    manualEntry?: boolean;
    manualRescue?: boolean;
    phase?: CancelPhase;
    rescueReason?: string;
    lastSupplierError?: string;
}

export interface CancelStatusResponse {
    cancelTransactionId: string;
    bookingId: string;
    status: CancelStatus;
    modifyType: CancelMode; // doc names this field modifyType on the cancel /status payload
    isTerminal: boolean;
    flowType?: string;
    tripType?: string;
    ptrType?: PtrType;
    customerTotal?: string;
    paymentStatus?: string;
    voidingWindowExpiresAt?: string;
    expiresAt?: string;
    cancellationReason?: string;
    phase?: CancelPhase;
    sectors: CancelSector[];
    passengers: CancelPassenger[];
    passengerCount?: number;
    booking?: CancelBookingInfo;
    quoteDetails?: CancelQuoteDetails;
    createdAt: string;
    updatedAt: string;
}

export interface ApiEnvelope<T> {
    status: number;
    message?: string;
    data: T;
}

const TERMINAL_CANCEL_STATUSES: ReadonlySet<CancelStatus> = new Set<CancelStatus>([
    "CANCELLED",
    "CANCEL_FAILED",
    "QUOTE_EXPIRED",
    "AUTO_CANCELLED",
    "FAILED",
]);

export function isTerminalCancelStatus(status: CancelStatus): boolean {
    return TERMINAL_CANCEL_STATUSES.has(status);
}

// Normalize ptrType at the FE boundary (backend already maps MondeeVoid -> Void,
// but guard anyway so the UI never shows a supplier-specific label).
export function normalizePtrType(ptr?: string | null): PtrType | undefined {
    if (!ptr) return undefined;
    return ptr.toLowerCase().includes("refund") ? "Refund" : "Void";
}
//********TYPES ENDS*********/

//********SERVICE FUNCTIONS STARTS*********/

// Workhorse: full denormalized snapshot of the cancel row. Mount + polling.
export const getCancelStatus = (
    cancelTransactionId: string,
    opts: object = {}
): Promise<ApiEnvelope<CancelStatusResponse>> =>
    apiGet(`${CANCEL_BASE}/${cancelTransactionId}/status`, { ...opts, traceId: genTraceId() });

// Generate the cancellation quote (AUTO suppliers). On AUTO failure the row
// routes to QUOTE_PENDING + MANUAL rescue (status in the response tells which).
export interface GenerateQuoteBody {
    cancelTransactionId: string;
}
export interface GenerateQuoteResponse {
    cancelTxnId: string;
    status: CancelStatus;
    quoteSource?: CancelQuoteSource;
    ptrType?: string;
    ptrId?: number | string;
    mfRef?: string;
    slaInMinutes?: number;
    sectors?: CancelSector[];
    sectorQuotes?: CancelSectorQuote[];
    quoteExpiresAt?: string;
    expiresAt?: string;
}
export const generateQuote = (
    body: GenerateQuoteBody,
    opts: object = {}
): Promise<ApiEnvelope<GenerateQuoteResponse>> =>
    apiPost(`${CANCEL_BASE}/generate-quote`, body, { ...opts, traceId: genTraceId() });

// Ops handoff: submit the supplier quote captured offline. Hits /send-quote with
// a sectorQuotes body. Backend stores it then auto-progresses dispatch logic.
// The FE sends ONLY these six inputs per pax (SMB-3206). The backend computes and
// returns totalAmount, refundAmount, cancelMarkup, cancelServiceFee, seatAmount,
// seatNumber -- do NOT send totalAmount / refundAmount (they are ignored).
export interface ManualQuoteInputRate {
    baseAmount: number;
    taxes: number;
    markup: number;
    serviceFee: number;
    discount: number;
    penalty: number;
}
export interface SubmitManualQuoteBody {
    cancelTransactionId: string;
    ptrType: PtrType;
    currency: string;
    sectorQuotes: Array<{
        transactionId: string;
        paxRates: Partial<Record<PaxType, ManualQuoteInputRate>>;
    }>;
}
export interface SendQuoteResponse {
    cancelTxnId: string;
    status: CancelStatus;
    expiresAt?: string;
    emailSent?: boolean;
    emailRecipient?: string;
    emailError?: string | null;
}
export const submitManualQuote = (
    body: SubmitManualQuoteBody,
    opts: object = {}
): Promise<ApiEnvelope<SendQuoteResponse>> =>
    apiPost(`${CANCEL_BASE}/send-quote`, body, { ...opts, traceId: genTraceId() });

// Dispatch the (already-ready) quote to the customer. Hits /send-quote with NO
// sectorQuotes body -- backend treats it as a plain dispatch.
export interface SendQuoteBody {
    cancelTransactionId: string;
    agentId?: string;
}
export const sendQuote = (
    body: SendQuoteBody,
    opts: object = {}
): Promise<ApiEnvelope<SendQuoteResponse>> =>
    apiPost(`${CANCEL_BASE}/send-quote`, body, { ...opts, traceId: genTraceId() });

// Customer approves (MyTrip). Wrapped for completeness.
export interface ApproveCancelBody {
    cancelTransactionId: string;
    itineraryId: string;
    userId: string;
}
export const approveCancel = (
    body: ApproveCancelBody,
    opts: object = {}
): Promise<ApiEnvelope<{ cancelTxnId: string; status: CancelStatus; ptrType?: string; quoteSource?: CancelQuoteSource }>> =>
    apiPost(`${CANCEL_BASE}/approve`, body, { ...opts, traceId: genTraceId() });

// Customer declines (MyTrip). Wrapped for completeness.
export interface DeclineCancelBody {
    cancelTransactionId: string;
    itineraryId: string;
    userId: string;
}
export const declineCancel = (
    body: DeclineCancelBody,
    opts: object = {}
): Promise<ApiEnvelope<{ cancelTxnId: string; status: CancelStatus }>> =>
    apiPost(`${CANCEL_BASE}/decline`, body, { ...opts, traceId: genTraceId() });

// Execute the supplier cancel (AUTO only). May return CANCELLING (poll) or
// auto-progress to REFUND_PENDING when the supplier confirms inline.
export interface ExecuteCancelBody {
    cancelTransactionId: string;
    agentId?: string;
}
export interface ExecuteCancelResponse {
    cancelTxnId: string;
    status: CancelStatus;
    supplierInternalStatus?: string;
    ptrType?: string;
    ptrId?: number | string;
    sectorQuotes?: CancelSectorQuote[];
    bookingTotals?: CancelBookingTotals;
}
export const executeCancel = (
    body: ExecuteCancelBody,
    opts: object = {}
): Promise<ApiEnvelope<ExecuteCancelResponse>> =>
    apiPost(`${CANCEL_BASE}/execute`, body, { ...opts, traceId: genTraceId() });

// Accounts/ops marks the refund processed -> CANCELLED terminal.
export interface MarkRefundedBody {
    cancelTransactionId: string;
    agentId?: string;
    refund_method?: string;
    refund_reference?: string;
}
export interface MarkRefundedResponse {
    cancelTxnId: string;
    status: CancelStatus;
    refundAmount?: string;
    customerEmailSent?: boolean;
    customerEmailError?: string | null;
}
export const markRefunded = (
    body: MarkRefundedBody,
    opts: object = {}
): Promise<ApiEnvelope<MarkRefundedResponse>> =>
    apiPost(`${CANCEL_BASE}/mark-refunded`, { ...body, outcome: "REFUNDED" }, { ...opts, traceId: genTraceId() });

// Ops marks the cancel failed (supplier rejected) -> CANCEL_FAILED terminal.
// Allowed from QUOTE_PENDING / CUSTOMER_APPROVED / CANCELLING. failureReason required.
export interface MarkCancelFailedBody {
    cancelTransactionId: string;
    failureReason: string;
    agentId?: string;
}
export interface MarkCancelFailedResponse {
    cancelTxnId: string;
    status: CancelStatus;
    outcome: RefundOutcome;
    failureReason: string;
    customerEmailSent?: boolean;
    customerEmailError?: string | null;
}
export const markCancelFailed = (
    body: MarkCancelFailedBody,
    opts: object = {}
): Promise<ApiEnvelope<MarkCancelFailedResponse>> =>
    apiPost(`${CANCEL_BASE}/mark-refunded`, { ...body, outcome: "FAILED" }, { ...opts, traceId: genTraceId() });
//********SERVICE FUNCTIONS ENDS*********/
